import React, { useContext } from "react"
import { AuthContext } from "../providers/auth"
import { Switch, Route, Redirect } from "react-router-dom"
import { auth } from "../firebase"

/**
 * Route components
 */
import Error404 from "../../containers/errors/404"
import Clients from "../../containers/clients/clients"
import Client from "../../containers/client/client"
import Targets from "../../containers/targets/targets"
import Enquiries from "../../containers/enquiries/enquiries"
import Bookings from "../../containers/bookings/bookings"
import BookingWrap from "../../containers/booking/booking"
import EnquiryPageWrap from "../../containers/enquiry/enquiry"
import StaffEnquiries from "../../containers/staff/staff"
import Stats from "../../containers/stats/stats"
import ConfirmationsReport from "../../containers/confirmations-report/confirmations-report"
import Balances from "../../containers/balances/balances"
import Suppliers from "../../containers/suppliers/suppliers"
import Travellers from "../../containers/travellers/travellers"
import Partners from "../../containers/partners/partners"
import Partner from "../../containers/partner/partner"
import Leaderboard from "../../containers/leaderboard/leaderboard"
import Internal from "../../containers/internal/internal"
import MergeClients from "../../containers/merge-clients/merge-clients"
import Data from "../../containers/data/data"
import Cancellations from "../../containers/cancellations/cancellations"
import Deposits from "../../containers/deposits/deposits"
import Holidays from "../../containers/cms/holidays/holidays"
import EditHoliday from "../../containers/cms/holidays/edit/edit"
import Quoting from "../../containers/quoting/quoting"
import QuoteAssistant from "../../containers/quote-assistant/quote-assistant"
import Courses from "../../containers/cms/courses/courses"
import EditCourse from "../../containers/cms/courses/edit/edit"
import DeletedEnquiries from "../../containers/deleted-enquiries/deleted-enquiries"
import EnquiryAssignments from "../../containers/enquiry-assignments/enquiry-assignments"
import EnquiriesReceived from "../../containers/enquiries-received/enquiries-received"
import MyQuotes from "../../containers/my-quotes/my-quotes"
import Flights from "../../containers/flights/flights"
import Buggies from "../../containers/buggies/buggies"
import AllQuotes from "../../containers/quotes/quotes"
import GiftVouchers from "../../containers/gift-vouchers/gift-vouchers"
import ParsingQueue from "../../containers/parsing-queue/parsing-queue"
import Charting from "../../containers/charting/charting"
import Transfers from "../../containers/transfers/transfers"
import ClientReceipts from "../../containers/client-receipts/client-receipts"
import Tracking from "../../containers/tracking/tracking"
import TrackingAnalytics from "../../containers/tracking-analytics/tracking-analytics"
import SupplierReceipts from "../../containers/supplier-receipts/supplier-receipts"
import CurrenciesOwed from "../../containers/currencies-owed/currencies-owed"
import MarginAttribution from "../../containers/margin-attribution/margin-attribution"
import BookingMargins from "../../containers/booking-margins/booking-margins"
import ItemisedStats from "../../containers/itemised-stats/itemised-stats"
import DocumentUploads from "../../containers/document-uploads/document-uploads"
import HistoricalTargets from "../../containers/historical-targets/historical-targets"
import ManagerAnalytics from "../../containers/manager-analytics/manager-analytics"
import ConversionsBreakdown from "../../containers/conversions-breakdown/conversions-breakdown"
import CommissionSettlement from "../../containers/commission-settlement/commission-settlement"
import CommissionReportNew from "../../containers/commission-report-new/commission-report-new"
import PaymentLinks from "../../containers/payment-links/payment-links"
import { Masters2025, Masters2026, Masters2027 } from "../../containers/events"
import EnquiryStats from "../../containers/enquiry-stats/stats"
import StablefordPoints from "../../containers/stableford-points/stableford-points"
import StablefordTransfer from "../../containers/stableford-transfer/stableford-transfer"
import SegregatedConversions from "../../containers/segregated-conversions/segregated-conversions"
import ConversionsByChannel from "../../containers/conversions-by-channel/conversions-by-channel"
import TargetTracker from "../../containers/target-tracker/target-tracker"
import ReopenedConversions from "../../containers/reopened-conversions/reopened-conversions"
import LogViewer from "../../containers/log-viewer/log-viewer"
import Sabine from "../../containers/sabine/sabine"
import BookingsReceived from "../../containers/bookings-received/bookings-received"
import AssignBookings from "../../containers/assign-bookings/assign-bookings"
import CommunicationsMailboxes from "../../containers/communications/mailboxes"
import CommunicationsMailbox from "../../containers/communications/mailbox"
import EnquiryIngest from "../../containers/enquiry-ingest/enquiry-ingest"
import Amendments from "../../containers/amendments/amendments"
import Amendment from "../../containers/amendments/amendment"
import Design from "../../containers/design/design"
import DatabaseExports from "../../containers/database-exports/database-exports"
import CaseLookup from "../../containers/case-lookup/case-lookup"
import StatisticHighlights from "../../containers/statistic-highlights/statistic-highlights"
import AiTooling from "../../containers/ai-tooling/ai-tooling"
import { isSuperAdminAllowlisted } from "../exports/super-admin-allowlist"

/**
 * Returns the routes for staff on the app
 */
function PageRoutes() {
	/**
	 * Deconstruct the user object from the auth context
	 */
	const { user } = useContext(AuthContext)
	const canAccessStablefordTransfer = user?.is_super_admin && isSuperAdminAllowlisted(auth.currentUser?.uid)
	const canAccessCaseLookup = user?.is_super_admin && isSuperAdminAllowlisted(auth.currentUser?.uid)
	const canAccessStatisticHighlights = user?.is_super_admin && isSuperAdminAllowlisted(auth.currentUser?.uid)

	return (
		<Switch>
			{/* Admin route for targets */}
			{user?.is_admin && (
				<Route
					path="/targets"
					component={Targets}
				/>
			)}
			{(user?.is_admin || user?.is_manager) && (
				<Route
					path="/historical-targets"
					component={HistoricalTargets}
				/>
			)}

			{/* Manager analytics route for admins and managers */}
			{(user?.is_admin || user?.is_manager) && (
				<Route
					path="/manager-analytics"
					component={ManagerAnalytics}
				/>
			)}

			{/* Admin route for internal settings */}
			{user?.is_admin && (
				<Route
					path="/internal"
					component={Internal}
				/>
			)}

			{/* Admin route for reopened conversions */}
			{user?.is_admin && (
				<Route
					path="/reopened-enquiries"
					component={ReopenedConversions}
				/>
			)}

			{/* Admin route for quoted enquiries */}
			{(user?.is_admin ||
				user?.is_super_admin ||
				auth.currentUser?.uid === "QHFkCVacp3VPPYbpF3H8yw9Ufl72") && (
				<Route
					path="/quoted-inquiries"
					component={EnquiriesReceived}
				/>
			)}

			{/* Backwards-compat redirects for the old quoted enquiries URLs */}
			{(user?.is_admin ||
				user?.is_super_admin ||
				auth.currentUser?.uid === "QHFkCVacp3VPPYbpF3H8yw9Ufl72") && (
				<Redirect
					from="/enquiries-received"
					to="/quoted-inquiries"
					exact
				/>
			)}
			{(user?.is_admin ||
				user?.is_super_admin ||
				auth.currentUser?.uid === "QHFkCVacp3VPPYbpF3H8yw9Ufl72") && (
				<Redirect
					from="/inquiries-received"
					to="/quoted-inquiries"
					exact
				/>
			)}

			{/* Backwards-compat redirect */}
			{user?.is_admin && (
				<Route
					path="/reopened-conversions"
					render={() => <Redirect to="/reopened-enquiries" />}
				/>
			)}

			{/* Merge clients route */}
			{user?.is_super_admin && (
				<Route
					path="/merge-clients"
					component={MergeClients}
				/>
			)}

			{/* Admin route for data settings */}
			{user?.is_admin && (
				<Route
					path="/data"
					component={Data}
				/>
			)}

			{!user?.crm_manager && (
				<Route
					path="/charting"
					component={Charting}
				/>
			)}

			{/* Admin route for viewing deleted enquiries */}
			{[
				"CGOHCdUwFAOe13OSTY2D9qdO2LX2",
				"iUlTXjfx1OZEPBhPosh6rJt3CKe2",
				"7GFYd2EvA9OiZxcufgbz5CRcssu1",
				"fM61i6PTzzZPtCGolcx870r9cCo2",
				"L334Rwj0YiRWSuCZLlixoBbsxwl2",
				"ndo3zxkvoMdYJYg7xyeqSQshDQw1",
				"8lIXQuliWhPJXrgi0rM7dJ3e9kM2",
				"9ChsmNaiYkdQGjm3JbEbqY6mIbR2",
				"FbeBRh8bIGb9L6aRLLJSttvutQ33",
				"8lIXQuliWhPJXrgi0rM7dJ3e9kM2",
				"p4VG2EgFFQS8fsaBc0d9peNWaD52",
				"L334Rwj0YiRWSuCZLlixoBbsxwl2",
				"gWd7pXtuAMcXrywMUTw2vRbid1j1",
				"M1EUhnht2mQc9pucFAB7A8rWFul1",
				"Y8eKcR9e2mgrSX4Bqj7yIGcacgm2",
				"NRmE5zXkW6SwfouZFYiYmu8WUqJ2",
			].includes(auth.currentUser.uid) && (
				<Route
					path="/deleted-enquiries"
					component={DeletedEnquiries}
				/>
			)}

			{/* Admin route for viewing deleted enquiries */}
			{[
				"CGOHCdUwFAOe13OSTY2D9qdO2LX2",
				"iUlTXjfx1OZEPBhPosh6rJt3CKe2",
				"7GFYd2EvA9OiZxcufgbz5CRcssu1",
				"fM61i6PTzzZPtCGolcx870r9cCo2",
				"L334Rwj0YiRWSuCZLlixoBbsxwl2",
				"neW9eVfyCNW5ILndUxMwNrXQxJD3",
				"8lIXQuliWhPJXrgi0rM7dJ3e9kM2",
				"9ChsmNaiYkdQGjm3JbEbqY6mIbR2",
				"FbeBRh8bIGb9L6aRLLJSttvutQ33",
				"8lIXQuliWhPJXrgi0rM7dJ3e9kM2",
				"p4VG2EgFFQS8fsaBc0d9peNWaD52",
				"L334Rwj0YiRWSuCZLlixoBbsxwl2",
				"gWd7pXtuAMcXrywMUTw2vRbid1j1",
				"M1EUhnht2mQc9pucFAB7A8rWFul1",
				"Y8eKcR9e2mgrSX4Bqj7yIGcacgm2",
			].includes(auth.currentUser.uid) && (
				<Route
					path="/enquiry-assignments"
					component={EnquiryAssignments}
				/>
			)}

			{/* Admin route for viewing all quotes */}
			{[
				"CGOHCdUwFAOe13OSTY2D9qdO2LX2",
				"iUlTXjfx1OZEPBhPosh6rJt3CKe2",
				"7GFYd2EvA9OiZxcufgbz5CRcssu1",
				"fM61i6PTzzZPtCGolcx870r9cCo2",
				"L334Rwj0YiRWSuCZLlixoBbsxwl2",
				"neW9eVfyCNW5ILndUxMwNrXQxJD3",
				"8lIXQuliWhPJXrgi0rM7dJ3e9kM2",
				"9ChsmNaiYkdQGjm3JbEbqY6mIbR2",
				"FbeBRh8bIGb9L6aRLLJSttvutQ33",
				"8lIXQuliWhPJXrgi0rM7dJ3e9kM2",
				"p4VG2EgFFQS8fsaBc0d9peNWaD52",
				"L334Rwj0YiRWSuCZLlixoBbsxwl2",
				"gWd7pXtuAMcXrywMUTw2vRbid1j1",
				"M1EUhnht2mQc9pucFAB7A8rWFul1",
				"Y8eKcR9e2mgrSX4Bqj7yIGcacgm2",
			].includes(auth.currentUser.uid) && (
				<Route
					path="/quotes"
					component={AllQuotes}
				/>
			)}

			{/* App page routes for staff accounts */}
			{!user?.crm_manager && (
				<Route
					path="/clients"
					component={Clients}
				/>
			)}
			{!user?.crm_manager && (
				<Route
					path="/client/:id"
					component={Client}
				/>
			)}
			{!user?.crm_manager && (
				<Route
					path="/enquiries"
					component={Enquiries}
				/>
			)}
			{!user?.crm_manager && (
				<Route
					path="/enquiry/:enquiryID"
					component={EnquiryPageWrap}
				/>
			)}
			{!user?.crm_manager && (
				<Route
					path="/bookings"
					component={Bookings}
				/>
			)}
			{!user?.crm_manager && (
				<Route
					path="/booking/:id"
					component={BookingWrap}
				/>
			)}
			{!user?.crm_manager && (
				<Route
					path="/staff"
					component={StaffEnquiries}
				/>
			)}
			{!user?.crm_manager && (
				<Route
					path="/stats"
					component={Stats}
				/>
			)}
			{!user?.crm_manager && (
				<Route
					path="/confirmations-report"
					component={ConfirmationsReport}
				/>
			)}
			{!user?.crm_manager && (
				<Route
					path="/enquiry-stats"
					component={EnquiryStats}
				/>
			)}
			{!user?.crm_manager && (
				<Route
					path="/flights"
					component={Flights}
				/>
			)}
			{!user?.crm_manager && (
				<Route
					path="/transfers"
					component={Transfers}
				/>
			)}
			{!user?.crm_manager && (
				<Route
					path="/buggies"
					component={Buggies}
				/>
			)}
			{!user?.crm_manager && (
				<Route
					path="/conversions"
					component={SegregatedConversions}
				/>
			)}
			{!user?.crm_manager && (
				<Route
					path="/balances"
					component={Balances}
				/>
			)}
			{!user?.crm_manager && (
				<Route
					path="/suppliers"
					component={Suppliers}
				/>
			)}
			{!user?.crm_manager && (
				<Route
					path="/travellers"
					component={Travellers}
				/>
			)}
			{/* Admin route for client receipts */}
			{user?.is_admin && (
				<Route
					path="/client-receipts"
					component={ClientReceipts}
				/>
			)}
			{user?.is_admin && (
				<Route
					path="/supplier-receipts"
					component={SupplierReceipts}
				/>
			)}
			{user?.is_admin && (
				<Route
					path="/margin-attribution"
					component={MarginAttribution}
				/>
			)}
			{user?.is_admin && (
				<Route
					path="/booking-margins"
					component={BookingMargins}
				/>
			)}
			{user?.is_admin && (
				<Route
					path="/conversions-breakdown"
					component={ConversionsBreakdown}
				/>
			)}
			{user?.is_admin && (
				<Route
					path="/conversions-by-channel"
					component={ConversionsByChannel}
				/>
			)}
			{!user?.crm_manager && (
				<Route
					path="/my-quotes"
					component={MyQuotes}
				/>
			)}

			{[
				"CGOHCdUwFAOe13OSTY2D9qdO2LX2",
				"iUlTXjfx1OZEPBhPosh6rJt3CKe2",
				"7GFYd2EvA9OiZxcufgbz5CRcssu1",
				"fM61i6PTzzZPtCGolcx870r9cCo2",
				"L334Rwj0YiRWSuCZLlixoBbsxwl2",
				"9ChsmNaiYkdQGjm3JbEbqY6mIbR2",
				"Z6EM3kFyJ8QiJMzMsYm6TwrWaUH3",
				"FbeBRh8bIGb9L6aRLLJSttvutQ33",
				"p4VG2EgFFQS8fsaBc0d9peNWaD52",
				"ndo3zxkvoMdYJYg7xyeqSQshDQw1",
				"Y8eKcR9e2mgrSX4Bqj7yIGcacgm2",
			].includes(auth.currentUser.uid) && (
				<Route
					path="/partners"
					component={Partners}
				/>
			)}

			{[
				"CGOHCdUwFAOe13OSTY2D9qdO2LX2",
				"iUlTXjfx1OZEPBhPosh6rJt3CKe2",
				"7GFYd2EvA9OiZxcufgbz5CRcssu1",
				"fM61i6PTzzZPtCGolcx870r9cCo2",
				"L334Rwj0YiRWSuCZLlixoBbsxwl2",
				"9ChsmNaiYkdQGjm3JbEbqY6mIbR2",
				"Z6EM3kFyJ8QiJMzMsYm6TwrWaUH3",
				"FbeBRh8bIGb9L6aRLLJSttvutQ33",
				"p4VG2EgFFQS8fsaBc0d9peNWaD52",
				"neW9eVfyCNW5ILndUxMwNrXQxJD3",
				"Y8eKcR9e2mgrSX4Bqj7yIGcacgm2",
			].includes(auth.currentUser.uid) && (
				<Route
					path="/partner/:collection/:id"
					component={Partner}
				/>
			)}

			{!user?.crm_manager && (
				<Route
					path="/leaderboard"
					component={Leaderboard}
				/>
			)}
			{/* {!user?.crm_manager && (
				<Route
					path="/booking-leaderboard"
					component={BookingLeaderboard}
				/>
			)} */}
			{!user?.crm_manager && (
				<Route
					path="/cancellations"
					component={Cancellations}
				/>
			)}
			<Route
				path="/deposits"
				component={Deposits}
			/>
			{/* Gift vouchers - all admins and super admins */}
			{(user?.is_admin || user?.is_super_admin) && (
				<Route
					path="/gift-vouchers"
					component={GiftVouchers}
				/>
			)}

			{/* Admin route for target tracker */}
			{user?.is_admin && (
				<Route
					path="/target-tracker"
					component={TargetTracker}
				/>
			)}

			{/* Admin route for detailed log viewer */}
			{auth.currentUser.uid === "CGOHCdUwFAOe13OSTY2D9qdO2LX2" && (
				<Route
					path="/log-viewer"
					component={LogViewer}
				/>
			)}

			{/* Admin route for parsing queue */}
			{(user?.is_admin || user?.is_accounts) && (
				<Route
					path="/invoice-parsing-queue"
					component={ParsingQueue}
				/>
			)}

			{!user?.crm_manager && (
				<Route
					path="/quoting"
					component={Quoting}
				/>
			)}

			{/* Quote Assistant - flow builder for which hotels to quote alongside an enquiry */}
			{!user?.crm_manager && (
				<Route
					path="/quote-assistant"
					component={QuoteAssistant}
				/>
			)}

			{[
				"CGOHCdUwFAOe13OSTY2D9qdO2LX2",
				"iUlTXjfx1OZEPBhPosh6rJt3CKe2",
				"7GFYd2EvA9OiZxcufgbz5CRcssu1",
				"fM61i6PTzzZPtCGolcx870r9cCo2",
				"L334Rwj0YiRWSuCZLlixoBbsxwl2",
				"9ChsmNaiYkdQGjm3JbEbqY6mIbR2",
				"Z6EM3kFyJ8QiJMzMsYm6TwrWaUH3",
				"FbeBRh8bIGb9L6aRLLJSttvutQ33",
				"p4VG2EgFFQS8fsaBc0d9peNWaD52",
				"neW9eVfyCNW5ILndUxMwNrXQxJD3",
				"Y8eKcR9e2mgrSX4Bqj7yIGcacgm2",
			].includes(auth.currentUser.uid) && (
				<Route
					path="/manage/holidays"
					component={Holidays}
				/>
			)}

			{[
				"CGOHCdUwFAOe13OSTY2D9qdO2LX2",
				"iUlTXjfx1OZEPBhPosh6rJt3CKe2",
				"7GFYd2EvA9OiZxcufgbz5CRcssu1",
				"fM61i6PTzzZPtCGolcx870r9cCo2",
				"L334Rwj0YiRWSuCZLlixoBbsxwl2",
				"9ChsmNaiYkdQGjm3JbEbqY6mIbR2",
				"Z6EM3kFyJ8QiJMzMsYm6TwrWaUH3",
				"FbeBRh8bIGb9L6aRLLJSttvutQ33",
				"p4VG2EgFFQS8fsaBc0d9peNWaD52",
				"ndo3zxkvoMdYJYg7xyeqSQshDQw1",
				"neW9eVfyCNW5ILndUxMwNrXQxJD3",
				"Y8eKcR9e2mgrSX4Bqj7yIGcacgm2",
			].includes(auth.currentUser.uid) && (
				<Route
					path="/manage/holiday/:id"
					component={EditHoliday}
				/>
			)}

			{[
				"CGOHCdUwFAOe13OSTY2D9qdO2LX2",
				"iUlTXjfx1OZEPBhPosh6rJt3CKe2",
				"7GFYd2EvA9OiZxcufgbz5CRcssu1",
				"fM61i6PTzzZPtCGolcx870r9cCo2",
				"L334Rwj0YiRWSuCZLlixoBbsxwl2",
				"9ChsmNaiYkdQGjm3JbEbqY6mIbR2",
				"Z6EM3kFyJ8QiJMzMsYm6TwrWaUH3",
				"FbeBRh8bIGb9L6aRLLJSttvutQ33",
				"p4VG2EgFFQS8fsaBc0d9peNWaD52",
				"ndo3zxkvoMdYJYg7xyeqSQshDQw1",
				"neW9eVfyCNW5ILndUxMwNrXQxJD3",
				"Y8eKcR9e2mgrSX4Bqj7yIGcacgm2",
			].includes(auth.currentUser.uid) && (
				<Route
					path="/manage/courses"
					component={Courses}
				/>
			)}

			{[
				"CGOHCdUwFAOe13OSTY2D9qdO2LX2",
				"iUlTXjfx1OZEPBhPosh6rJt3CKe2",
				"7GFYd2EvA9OiZxcufgbz5CRcssu1",
				"fM61i6PTzzZPtCGolcx870r9cCo2",
				"L334Rwj0YiRWSuCZLlixoBbsxwl2",
				"9ChsmNaiYkdQGjm3JbEbqY6mIbR2",
				"Z6EM3kFyJ8QiJMzMsYm6TwrWaUH3",
				"FbeBRh8bIGb9L6aRLLJSttvutQ33",
				"p4VG2EgFFQS8fsaBc0d9peNWaD52",
				"ndo3zxkvoMdYJYg7xyeqSQshDQw1",
				"neW9eVfyCNW5ILndUxMwNrXQxJD3",
				"Y8eKcR9e2mgrSX4Bqj7yIGcacgm2",
			].includes(auth.currentUser.uid) && (
				<Route
					path="/manage/course/:id"
					component={EditCourse}
				/>
			)}

			{/* Admin route for tracking */}
			{(user?.is_admin || auth.currentUser.uid === "V0EpXEeyopbFR8jR3AOflLSqeTM2") && (
				<Route
					path="/tracking"
					component={Tracking}
				/>
			)}

			{/* Admin route for viewing tracking analytics */}
			{(user?.is_admin || user?.crm_manager || auth.currentUser.uid === "V0EpXEeyopbFR8jR3AOflLSqeTM2") && (
				<Route
					path="/utm-parameters"
					component={TrackingAnalytics}
				/>
			)}

			{/* Admin route for currencies owed */}
			{user?.is_admin && (
				<Route
					path="/currencies-owed"
					component={CurrenciesOwed}
				/>
			)}

			{/* Admin route for itemised stats */}
			{user?.is_admin && (
				<Route
					path="/itemised-stats"
					component={ItemisedStats}
				/>
			)}

			{/* Admin route for document uploads */}
			{user?.is_admin && (
				<Route
					path="/document-uploads"
					component={DocumentUploads}
				/>
			)}

			{/* Masters 2025 event - admins and specific allowlisted users */}
			{(user?.is_admin || auth.currentUser?.uid === "stpLFe3M7GdRtK1kLghLXAwSFom1") && (
				<Route
					path="/events/masters-2025"
					component={Masters2025}
				/>
			)}

			{/* Masters 2026 event - admins and specific allowlisted users */}
			{(user?.is_admin || auth.currentUser?.uid === "stpLFe3M7GdRtK1kLghLXAwSFom1") && (
				<Route
					path="/events/masters-2026"
					component={Masters2026}
				/>
			)}

			{/* Masters 2027 event - admins and specific allowlisted users */}
			{(user?.is_admin || auth.currentUser?.uid === "stpLFe3M7GdRtK1kLghLXAwSFom1") && (
				<Route
					path="/events/masters-2027"
					component={Masters2027}
				/>
			)}

			{/* Legacy commission settlement page — kept reachable at /commission-settlement-old
			    but no longer linked from the nav. Replaced by /commission-report. */}
			{user?.is_admin && (
				<Route
					path="/commission-settlement-old"
					component={CommissionSettlement}
				/>
			)}

			{/* Banded commission report — admins see everyone + all actions;
			    non-admin sales agents see only their own row + Generate report */}
			{!user?.crm_manager && (
				<Route
					path="/commission-report"
					component={CommissionReportNew}
				/>
			)}

			{/* Payment Links — standalone payment requests not tied to a booking */}
			<Route
				path="/payment-links"
				component={PaymentLinks}
			/>

			{/* Admin route for segregated conversions */}
			{user?.is_admin && (
				<Route
					path="/segregated-conversions"
					component={SegregatedConversions}
				/>
			)}

			{/* Admin route for commission settlement */}
			{user?.is_admin && (
				<Route
					path="/stableford-points"
					component={StablefordPoints}
				/>
			)}

			{/* Super admin route for moving Stableford points between clients.
			    Hidden from the sidebar; /stableford-transfer kept as a legacy alias. */}
			{canAccessStablefordTransfer && (
				<Route
					path={["/move-stableford-points", "/stableford-transfer"]}
					component={StablefordTransfer}
				/>
			)}

			{/* Super admin route for Sabine monthly report */}
			{user?.is_super_admin && (
				<Route
					path="/sabine"
					component={Sabine}
				/>
			)}

			{/* Hidden route for the case lookup tool: everything held against a
			    list of email addresses, for answering a legal request. Reads
			    Mailchimp through the local dev proxy, so it only functions under
			    npm start. Same allowlist as the other sensitive admin tools. */}
			{canAccessCaseLookup && (
				<Route
					path="/case-lookup"
					component={CaseLookup}
				/>
			)}

			{/* Confidential super-admin analytics dashboard. Hidden from the
			    sidebar; reachable by direct URL only, gated on the same
			    is_super_admin + UID allowlist as the Stableford transfer tool. */}
			{canAccessStatisticHighlights && (
				<Route
					path="/statistic-highlights"
					component={StatisticHighlights}
				/>
			)}

			{/* Hidden route - any staff account can view their own received
			    bookings; admins / super admins can also pick any sales agent */}
			<Route
				path="/bookings-received"
				component={BookingsReceived}
			/>

			{/* Admin route for reassigning bookings between sales agents */}
			{(user?.is_admin || user?.is_super_admin) && (
				<Route
					path="/assign-bookings"
					component={AssignBookings}
				/>
			)}

			{/* Communications-ingestion mailbox panel — restricted to a single
			    UID for now while the feature is in early development. Lift the
			    check to `user?.is_admin` once we're ready to roll it out wider. */}
			{auth.currentUser?.uid === "CGOHCdUwFAOe13OSTY2D9qdO2LX2" && (
				<Route
					path="/communications/mailboxes"
					component={CommunicationsMailboxes}
				/>
			)}

			{/* Per-mailbox inbox view — same UID restriction as above */}
			{auth.currentUser?.uid === "CGOHCdUwFAOe13OSTY2D9qdO2LX2" && (
				<Route
					path="/communications/mailbox/:mailboxID"
					component={CommunicationsMailbox}
				/>
			)}

			{/* Enquiry ingest — dev/preview surface for iterating on the
			    enquiry-parsing prompt + JSON schema. Same UID restriction as
			    the mailbox pages while the parser is in early development. */}
			{auth.currentUser?.uid === "CGOHCdUwFAOe13OSTY2D9qdO2LX2" && (
				<Route
					path="/enquiry-ingest"
					component={EnquiryIngest}
				/>
			)}

			{/* Amendments — gated on the per-user is_amendments flag, with admins
			    implicitly included. See docs/amendments-tab-drilling.md. */}
			{(user?.is_admin || user?.is_amendments) && (
				<Route
					path="/amendments/:amendmentID"
					component={Amendment}
				/>
			)}
			{(user?.is_admin || user?.is_amendments) && (
				<Route
					path="/amendments"
					component={Amendments}
					exact
				/>
			)}

			{/* Admin-only design sandbox */}
			{user?.is_admin && (
				<Route
					path="/design"
					component={Design}
				/>
			)}

			{/* Hidden, admin-only database exports (not in the nav) */}
			{user?.is_admin && (
				<Route
					path="/database-exports"
					component={DatabaseExports}
				/>
			)}

			{/* Super-admin AI Tooling — central place to author prompts, models,
			    and JSON schemas for the app's AI features. Backed by the
			    `ai_tooling` Firestore collection. */}
			{user?.is_super_admin && (
				<Route
					path="/ai-tooling"
					component={AiTooling}
				/>
			)}

			{/* Page redirects for staff accounts */}
			<Redirect
				from="/"
				to="/clients"
				exact
			/>
			<Redirect
				from="/login"
				to="/clients"
				exact
			/>
			{user?.crm_manager && (
				<Redirect
					from="/clients"
					to="/utm-parameters"
					exact
				/>
			)}

			{/* Error pages */}
			<Route
				path="*"
				component={Error404}
			/>
		</Switch>
	)
}

export default PageRoutes
